





import React, { useState, useCallback, useEffect, useMemo, lazy, Suspense } from 'react';
import { CartItem, Product, SubscriptionFrequency, Order, OrderStatus, User, Coupon, Customer, DeliveryType, PickupPoint, B2BProduct, Campaign, Subscriber, LegalModalType, CustomerGroup, Address } from './types';
import Header from './components/Header';
import Footer from './components/Footer';
import { ThemeProvider } from './context/ThemeContext';
import { SHIPPING_COST, API_BASE_URL } from './constants';
import { useRouter } from './hooks/useRouter';
import HomePage from './components/HomePage';
import Loader from './components/Loader';
import { ToastContainer } from 'react-toastify';
import { useToast } from './hooks/useToast';
import CookieConsent, { Cookies } from "react-cookie-consent";
// FIX: Import 'useTranslation' to provide the 't' function for internationalization.
import { useTranslation } from './hooks/useTranslation';

// Lazy-loaded components
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const CartModal = lazy(() => import('./components/CartModal'));
const CheckoutModal = lazy(() => import('./components/CheckoutModal'));
const ProductDetailPage = lazy(() => import('./components/ProductDetailPage'));
const CustomerLoginModal = lazy(() => import('./components/CustomerLoginModal'));
const CustomerAccountPage = lazy(() => import('./components/CustomerAccountPage'));
const OrderDetailModal = lazy(() => import('./components/OrderDetailModal'));
const AddToCartModal = lazy(() => import('./components/AddToCartModal'));

const LoadingFallback = () => (
  <div className="fixed inset-0 bg-white/50 dark:bg-slate-900/50 flex items-center justify-center z-[100]">
    <Loader />
  </div>
);

const AppContent: React.FC = () => {
  const { page, param, navigate } = useRouter();
  // FIX: Initialize the 't' function from the useTranslation hook to be used in the CookieConsent component.
  const { t } = useTranslation();
  
  const [isAdminView, setIsAdminView] = useState(false);
  const [isB2BView, setIsB2BView] = useState(false);
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
        const storedCart = localStorage.getItem('papirrullen-cart');
        return storedCart ? JSON.parse(storedCart) : [];
    } catch (error) {
        console.error("Failed to parse cart from localStorage", error);
        return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  
  // Checkout State
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryType>('shipping');
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPoint | null>(null);
  
  // Coupon State
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);

  // User State
  const [user, setUser] = useState<User | null>(() => {
    try {
        const storedUser = localStorage.getItem('papirrullen-user');
        return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
        console.error("Failed to parse user from localStorage", error);
        return null;
    }
  });



  // Modal State
  const [isCustomerLoginOpen, setIsCustomerLoginOpen] = useState(false);
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [isAddToCartModalOpen, setIsAddToCartModalOpen] = useState(false); 
  const [selectedProductForCart, setSelectedProductForCart] = useState<Product | null>(null); 

  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [b2bProducts, setB2bProducts] = useState<B2BProduct[]>([]);
  const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
  const [userOrders, setUserOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const currentProducts = useMemo(() => isB2BView ? b2bProducts : products, [isB2BView, products, b2bProducts]);
  const allProducts = useMemo(() => [...products, ...b2bProducts], [products, b2bProducts]);
  

  // --- DATA FETCHING ---
  const fetchPublicData = useCallback(async () => {
    try {
        const [productsRes, b2bProductsRes, pickupPointsRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/products`),
            fetch(`${API_BASE_URL}/api/b2b-products`),
            fetch(`${API_BASE_URL}/api/pickup-points`),
        ]);
        setProducts(await productsRes.json());
        setB2bProducts(await b2bProductsRes.json());
        setPickupPoints(await pickupPointsRes.json());
    } catch (error) {
        console.error("Failed to fetch public data:", error);
        useToast('Could not load data from the server.', 'error');
    }
  }, []);

  // Initial public data fetch
  useEffect(() => {
    setIsLoading(true);
    fetchPublicData().finally(() => setIsLoading(false));
  }, [fetchPublicData]);
  
  // The refetch handler is just the fetch function itself
  const handleProductsChange = fetchPublicData;
  const handlePickupPointsChange = fetchPublicData;

  useEffect(() => {
    const storedVisitor = localStorage.getItem('papirrullen-visitor');
    const updateMetric = async () => {
      await fetch(`${API_BASE_URL}/api/metric`);
    }

    let lastSeen = storedVisitor ? parseInt(storedVisitor) : 0;
    if( lastSeen < (Date.now()/1000)-(30+7*24*60*60*0) ) {
      try {
        updateMetric()
      }  finally {
        localStorage.setItem('papirrullen-visitor', (Date.now()/1000).toString())
      }
    }  
  });
  
  // Fetch user-specific data when user logs in
  useEffect(() => {
      const fetchUserOrders = async () => {
          if (!user) {
              setUserOrders([]);
              return;
          }
          try {
              const res = await fetch(`${API_BASE_URL}/api/orders`, {
                  headers: { 'x-user-id': user.id }
              });
              if (!res.ok) throw new Error('Failed to fetch orders');
              setUserOrders(await res.json());
          } catch (error) {
              console.error(error);
              useToast('Could not load your orders.', 'error');
          }
      };
      fetchUserOrders();
  }, [user]);
  
  useEffect(() => {
    const homePageSections = ['about', 'products', 'subscription', 'contact'];

    if (homePageSections.includes(page) && !param) {
      const element = document.getElementById(page);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      if (page !== 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [page, param]);

  // SEO: Dynamic Metadata and JSON-LD
  useEffect(() => {
    const updateMetadata = (title: string, description: string, imageUrl?: string) => {
      document.title = title;
      document.querySelector('meta[name="description"]')?.setAttribute('content', description);
      
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', title);
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', description);
      document.querySelector('meta[property="og:url"]')?.setAttribute('content', window.location.href);
      if (imageUrl) document.querySelector('meta[property="og:image"]')?.setAttribute('content', imageUrl);
      document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', description);
      if (imageUrl) document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', imageUrl);
    };
  
    const updateJsonLd = (data: object | null) => {
      const scriptTag = document.getElementById('json-ld');
      if (scriptTag) scriptTag.textContent = data ? JSON.stringify(data, null, 2) : '';
    };
  
    if (page === 'products' && param) {
      const product = allProducts.find(p => p.id === Number(param));
      if (product) {
          const title = `${product.name} - Papirrullen`;
          const description = product.description.substring(0, 160);
          const imageUrl = product.imageUrls[0];
          const priceGroup = user?.customerGroup || "Default";
          const price = product.prices[priceGroup];
          updateMetadata(title, description, imageUrl);
          updateJsonLd({
              '@context': 'https://schema.org', '@type': 'Product', name: product.name, description: product.description, image: product.imageUrls,
              sku: `PROD-${product.id}`, brand: { '@type': 'Brand', name: 'Papirrullen' },
              offers: { '@type': 'Offer', url: window.location.href, priceCurrency: 'DKK', price: price.toFixed(2), availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock', itemCondition: 'https://schema.org/NewCondition' },
          });
      }
    } else {
      const title = 'Papirrullen - Premium, Bæredygtigt Toiletpapir & Køkkenruller';
      const description = 'Køb højkvalitets, bæredygtigt toiletpapir og køkkenruller. Vælg engangskøb eller spar 15% med et fleksibelt abonnement. Levering i hele Danmark.';
      updateMetadata(title, description, 'https://papirrullen.clientdemo.app/assets/images/koala1.jpg');
      updateJsonLd({
          '@context': 'https://schema.org', '@type': 'Organization', name: 'Papirrullen', url: window.location.origin, logo: `${window.location.origin}/vite.svg`,
          contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', email: 'info@papirrullen.dk' }
      });
    }
  }, [page, param, allProducts, user]);

  useEffect(() => { localStorage.setItem('papirrullen-cart', JSON.stringify(cart)); }, [cart]);
  useEffect(() => {
    try {
        if (user) localStorage.setItem('papirrullen-user', JSON.stringify(user));
        else localStorage.removeItem('papirrullen-user');
    } catch (error) { console.error("Failed to save user to localStorage", error); }
  }, [user]);
  useEffect(() => { if (cart.length === 0) setAppliedCoupon(null); }, [cart]);

  // --- AUTH HANDLERS ---
  const handleLogin = useCallback(async (email: string, password: string): Promise<boolean> => {
      try {
          const response = await fetch(`${API_BASE_URL}/api/login`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }),
          });
          const result = await response.json();
          if (response.ok) {
              setUser(result.user);
              useToast(`Welcome back, ${result.user.name}!`);
              return true;
          } else { throw new Error(result.message); }
      } catch (error) {
          useToast((error as Error).message || 'Login failed.', 'error');
          return false;
      }
  }, []);

  const handleRegister = useCallback(async (name: string, email: string, password: string): Promise<boolean> => {
      try {
          const response = await fetch(`${API_BASE_URL}/api/register`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email, password }),
          });
          const newUser = await response.json();
          if (response.ok) {
              setUser(newUser);
              useToast('Account created successfully! Welcome.', 'success');
              return true;
          } else { throw new Error(newUser.message); }
      } catch (error) {
          useToast((error as Error).message || 'Registration failed', 'error');
          return false;
      }
  }, []);
  
  const handleLogout = () => {
    setUser(null);
    setAppliedCoupon(null);
    setIsAdminView(false);
    navigate('#/');
    useToast('You have been logged out.');
  };

  // --- CART HANDLERS ---
  const handleOpenAddToCartModal = useCallback((product: Product) => { setSelectedProductForCart(product); setIsAddToCartModalOpen(true); }, []);
  const handleAddToCart = useCallback((product: Product, quantity: number) => {
    const productInDb = allProducts.find(p => p.id === product.id);
    if (!productInDb || productInDb.stock < quantity) { useToast(`Cannot add ${product.name}, insufficient stock.`, 'error'); return; }
    
    const priceGroup = user?.customerGroup || "Default";
    const price = product.prices[priceGroup];

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      const newQuantity = (existingItem?.quantity || 0) + quantity;
      if (productInDb.stock < newQuantity) { useToast(`Only ${productInDb.stock} of ${product.name} available.`, 'error'); return prevCart; }
      if (existingItem) return prevCart.map(item => item.id === product.id ? { ...item, quantity: newQuantity } : item);
      
      const { prices, ...productDetails } = product;
      const newCartItem: CartItem = {
          ...productDetails,
          price: price, // Store the price for the user's group
          quantity: quantity,
          subscriptionFrequency: 'none',
      };
      return [...prevCart, newCartItem];
    });
    useToast(`${product.name} added to cart!`);
    setIsAddToCartModalOpen(false); setSelectedProductForCart(null); 
  }, [allProducts, user]);
  const handleUpdateCart = useCallback((productId: number, newQuantity: number) => {
    setCart(prevCart => {
      const itemToUpdate = prevCart.find(item => item.id === productId);
      if (!itemToUpdate || itemToUpdate.isGift) return prevCart;
      const productInDb = allProducts.find(p => p.id === productId);
      const availableStock = productInDb?.stock || 0;
      if (newQuantity > availableStock) { useToast(`Only ${availableStock} of ${itemToUpdate.name} available.`, 'error'); newQuantity = availableStock; }
      if (newQuantity <= 0) return prevCart.filter(item => item.id !== productId);
      return prevCart.map(item => item.id === productId ? { ...item, quantity: newQuantity } : item);
    });
  }, [allProducts]);
  const handleUpdateSubscriptionInCart = useCallback((productId: number, frequency: SubscriptionFrequency) => { setCart(prevCart => prevCart.map(item => item.id === productId ? { ...item, subscriptionFrequency: frequency } : item)); }, []);
  const handleProceedToCheckout = useCallback(() => {
    if (cart.length === 0) { useToast('Your cart is empty!', 'error'); return; }
    const stockErrors = cart.map(item => ({ ...item, availableStock: allProducts.find(p => p.id === item.id)?.stock ?? 0 })).filter(item => item.quantity > item.availableStock);
    if (stockErrors.length > 0) { useToast(`Insufficient stock for: ${stockErrors.map(item => item.name).join(', ')}. Please update your cart.`, 'error'); return; }
    setIsCartOpen(false); setIsCheckoutOpen(true); setDeliveryMethod('shipping'); setSelectedPickupPoint(null);
  }, [cart.length, allProducts, cart]);
  const cartSubtotal = useMemo(() => cart.reduce((sum, item) => { if (item.isGift) return sum; const discount = item.subscriptionFrequency !== 'none' ? 0.15 : 0; return sum + item.price * (1 - discount) * item.quantity }, 0), [cart]);
  const cartTotals = useMemo(() => {
      let discountAmount = 0; let shippingCost = (cart.length > 0 && deliveryMethod === 'shipping') ? SHIPPING_COST : 0;
      if (appliedCoupon) {
          if (appliedCoupon.type === 'percentage') discountAmount = cartSubtotal * (appliedCoupon.value! / 100);
          if (appliedCoupon.type === 'free_shipping') shippingCost = 0;
      }
      return { subtotal: cartSubtotal, discountAmount, shippingCost, grandTotal: cartSubtotal - discountAmount + shippingCost };
  }, [cartSubtotal, appliedCoupon, cart.length, deliveryMethod]);

  // --- COUPON HANDLERS ---
  const handleApplyCoupon = useCallback(async (code: string) => {
    try {
        const res = await fetch(`${API_BASE_URL}/api/coupons`);
        if (!res.ok) throw new Error("Could not fetch coupons");
        const allCoupons: Coupon[] = await res.json();
        
        const coupon = allCoupons.find(c => c.code.toUpperCase() === code.toUpperCase());
        if (!coupon) { useToast('Invalid coupon code.', 'error'); return; }
        const now = new Date();
        if (now < new Date(coupon.validFrom) || now > new Date(coupon.validUntil)) { useToast('This coupon is expired or not yet active.', 'error'); return; }
        if (!user) { useToast('You must be logged in to use this coupon.', 'error'); return; }
        
        const pastUses = userOrders.filter(o => o.customerName === user.name && o.couponsUsed.includes(coupon.code)).length;
        if (pastUses >= coupon.usesPerCustomer) { useToast('You have already used this coupon the maximum number of times.', 'error'); return; }
        
        setAppliedCoupon(coupon);
        useToast('Coupon applied successfully!', 'success');
        
        if (coupon.type === 'free_gift' && coupon.giftProductId) {
            const giftProduct = allProducts.find(p => p.id === coupon.giftProductId);
            if (giftProduct) {
                 const { prices, ...productDetails } = giftProduct;
                 const newCartItem: CartItem = {
                     ...productDetails,
                     price: 0,
                     quantity: 1,
                     subscriptionFrequency: 'none',
                     isGift: true,
                 };
                if (!cart.some(item => item.id === giftProduct.id && item.isGift)) {
                    setCart(prev => [...prev, newCartItem]);
                }
            }
        }
    } catch (error) {
        useToast((error as Error).message || 'Could not verify coupon', 'error');
    }
  }, [user, userOrders, cart, allProducts]);
  
  const handleRemoveCoupon = useCallback(() => {
      if (!appliedCoupon) return;
      if (appliedCoupon.type === 'free_gift' && appliedCoupon.giftProductId) setCart(prev => prev.filter(item => item.id !== appliedCoupon.giftProductId || !item.isGift));
      setAppliedCoupon(null);
      useToast('Coupon removed.');
  }, [appliedCoupon]);

  const handlePlaceOrder = useCallback(async (details: { name: string, address?: Address }) => {
      if (!user) { useToast('An unexpected error occurred. Please log in again.', 'error'); setIsCheckoutOpen(false); return; }
      const orderData = { 
        customerName: details.name, 
        customerEmail: user.email,
        deliveryAddress: details.address,
        items: cart, 
        total: cartTotals.grandTotal, 
        status: 'Pending' as OrderStatus, 
        date: new Date().toISOString().split('T')[0], 
        isSubscription: cart.some(item => item.subscriptionFrequency !== 'none'), 
        couponsUsed: appliedCoupon ? [appliedCoupon.code] : [], 
        discountAmount: cartTotals.discountAmount, 
        shippingCost: cartTotals.shippingCost, 
        deliveryType: deliveryMethod, 
        pickupPointId: selectedPickupPoint?.id, 
      };
      try {
          const response = await fetch(`${API_BASE_URL}/api/orders`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-user-id': user.id }, body: JSON.stringify(orderData), });
          const result = await response.json();
          if (!response.ok) throw new Error(result.message);
          setUserOrders(prev => [result.newOrder, ...prev]);
          setProducts(result.updatedProducts); setB2bProducts(result.updatedB2BProducts);
          useToast('Order placed successfully!', 'success'); setCart([]); setAppliedCoupon(null); setIsCheckoutOpen(false);
      } catch (error) {
          useToast(`Order failed: ${(error as Error).message}`, 'error'); setIsCheckoutOpen(false); setIsCartOpen(true);
      }
  }, [cart, cartTotals, user, appliedCoupon, deliveryMethod, selectedPickupPoint]);

  // --- ACCOUNT MGMT HANDLERS ---
  const handleUpdateProfile = useCallback(async (updates: Partial<Pick<User, 'name' | 'password' | 'address'>>) => {
      if (!user) return;
      try {
          const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates), });
          const updatedUser = await response.json();
          if (!response.ok) throw new Error(updatedUser.message);
          setUser(updatedUser);
          useToast('Profile updated successfully!', 'success');
      } catch (error) { useToast((error as Error).message || 'Profile update failed.', 'error'); }
  }, [user]);

  const handleCancelOrder = useCallback(async (orderId: string) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-user-role': 'admin' }, body: JSON.stringify({ status: 'Cancelled' }), });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message);
        setUserOrders(prev => prev.map(o => o.id === orderId ? result.updatedOrder : o));
        setProducts(result.updatedProducts); setB2bProducts(result.updatedB2BProducts);
        useToast('Order has been cancelled.', 'success');
      } catch (error) { useToast((error as Error).message, 'error'); }
  }, []);

  const handleRequestReturn = useCallback((orderId: string) => {
      setUserOrders(prev => prev.map(o => (o.id === orderId && (o.status === 'Shipped' || o.status === 'Delivered') ? { ...o, status: 'Cancelled' } : o)));
      useToast('Return requested. You will receive an email with instructions.', 'success');
  }, []);

  const handleUpdateSubscription = useCallback(async (orderId: string, itemId: number, updates: Partial<{ subscriptionFrequency: SubscriptionFrequency; subscriptionStatus: 'active' | 'paused' | 'cancelled' }>) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/subscription`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ itemId, updates }), });
        const updatedOrder = await response.json();
        if (!response.ok) throw new Error(updatedOrder.message);
        setUserOrders(prev => prev.map(o => o.id === orderId ? updatedOrder : o));
        useToast('Subscription updated!', 'success');
      } catch(error) { useToast((error as Error).message || 'Failed to update subscription', 'error'); }
  }, []);

  const handleRemoveItemFromOrder = useCallback(async (orderId: string, itemId: number) => {
    if (!user) { useToast('You must be logged in.', 'error'); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': user.id }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message);

      setUserOrders(prev => prev.map(o => o.id === orderId ? result.updatedOrder : o));
      setProducts(result.updatedProducts);
      setB2bProducts(result.updatedB2BProducts);
      
      if (viewingOrder?.id === orderId) {
        setViewingOrder(result.updatedOrder);
      }
      
      useToast('Item removed from order.', 'success');
    } catch (error) {
      useToast((error as Error).message, 'error');
    }
  }, [user, viewingOrder]);
  
  const handleOpenOrderDetailModal = (order: Order) => { setViewingOrder(order); setIsOrderDetailModalOpen(true); };
  
  // --- PAGE RENDERER ---
  const renderPage = () => {
    if (isLoading) return <div className="flex justify-center items-center h-screen"><p>Loading application data...</p></div>
    
    if (user?.isAdmin && isAdminView) {
      return (
        <Suspense fallback={<LoadingFallback />}>
            <AdminPanel 
              products={products}
              user={user}
              onProductsChange={handleProductsChange}
              onPickupPointsChange={handlePickupPointsChange}
            />
        </Suspense>
      );
    }
    
    switch (page) {
      case 'home':
        return <HomePage products={currentProducts} onOpenAddToCartModal={handleOpenAddToCartModal} isB2BView={isB2BView} user={user} />;
      case 'products':
        const selectedProduct = allProducts.find(p => p.id === Number(param));
        return selectedProduct ? (<Suspense fallback={<LoadingFallback />}><ProductDetailPage product={selectedProduct} allProducts={products} onOpenAddToCartModal={handleOpenAddToCartModal} isB2BView={isB2BView} user={user} /></Suspense>) 
                                : <HomePage products={currentProducts} onOpenAddToCartModal={handleOpenAddToCartModal} isB2BView={isB2BView} user={user} />;
      case 'account':
        return user ? (<Suspense fallback={<LoadingFallback />}>
                <CustomerAccountPage user={user} orders={userOrders} onUpdateProfile={handleUpdateProfile} onCancelOrder={handleCancelOrder} onRequestReturn={handleRequestReturn} onUpdateSubscription={handleUpdateSubscription} onViewOrder={handleOpenOrderDetailModal} />
            </Suspense>) : <HomePage products={currentProducts} onOpenAddToCartModal={handleOpenAddToCartModal} isB2BView={isB2BView} user={user} />;
      default:
        return <HomePage products={currentProducts} onOpenAddToCartModal={handleOpenAddToCartModal} isB2BView={isB2BView} user={user} />;
    }
  }

  return (
      <div className="relative text-brand-dark dark:text-slate-300 transition-colors duration-300">
        <Header cartItemCount={cart.reduce((sum, item) => sum + item.quantity, 0)} onCartClick={() => setIsCartOpen(true)} isAdminView={isAdminView} onToggleAdminView={() => setIsAdminView(!isAdminView)} isB2BView={isB2BView} onToggleB2BView={() => setIsB2BView(!isB2BView)} hasAdminAccess={user?.isAdmin} user={user} onLoginClick={() => setIsCustomerLoginOpen(true)} onLogout={handleLogout} navigate={navigate} />
        <main>{renderPage()}</main>
        <Footer />
        
        {/* Modals */}
        <Suspense fallback={<LoadingFallback />}>
            {isCartOpen && <CartModal cartItems={cart} products={allProducts} onClose={() => setIsCartOpen(false)} onUpdateCart={handleUpdateCart} onCheckout={handleProceedToCheckout} onApplyCoupon={handleApplyCoupon} onRemoveCoupon={handleRemoveCoupon} appliedCoupon={appliedCoupon} totals={cartTotals} />}
            {isCheckoutOpen && <CheckoutModal user={user} onLogin={handleLogin} onRegister={handleRegister} cartItems={cart} onClose={() => setIsCheckoutOpen(false)} onPlaceOrder={handlePlaceOrder} totals={cartTotals} appliedCoupon={appliedCoupon} onUpdateSubscription={handleUpdateSubscriptionInCart} pickupPoints={pickupPoints} deliveryMethod={deliveryMethod} setDeliveryMethod={setDeliveryMethod} selectedPickupPoint={selectedPickupPoint} setSelectedPickupPoint={setSelectedPickupPoint} />}
            {isCustomerLoginOpen && <CustomerLoginModal onClose={() => setIsCustomerLoginOpen(false)} onLogin={handleLogin} />}
            {isOrderDetailModalOpen && viewingOrder && <OrderDetailModal order={viewingOrder} onClose={() => setIsOrderDetailModalOpen(false)} onCancelOrder={handleCancelOrder}  onRemoveItemFromOrder={handleRemoveItemFromOrder} />}
            {isAddToCartModalOpen && selectedProductForCart && <AddToCartModal product={selectedProductForCart} user={user} onClose={() => { setIsAddToCartModalOpen(false); setSelectedProductForCart(null); }} onConfirmAddToCart={handleAddToCart} />}
        </Suspense>
        <ToastContainer />
        <CookieConsent>{t('cookie_consent')}</CookieConsent>
      </div>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppContent />
  </ThemeProvider>
);

export default App;